import { useEffect, useState, useRef, useCallback, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Modal } from '../components/Modal'
import { VehicleStateCard } from '../components/VehicleStateCard'
import { THRESHOLDS, WEBSOCKET, PRECISION, INTERVALS, TIMEOUTS } from '../constants'
import { formatRuntime, formatChartDate, formatChartTime, isStale, formatAge } from '../utils/formatters'
import type { ChargerStatus, ClimateSensor, DashboardData, DataTimestamps, VehicleState } from '../types'
import './Dashboard.css'

interface BatteryUpdatePayload {
  soc: number
  voltage: number
  current?: number
  temperature?: number
  timestamp: string
  power_watts?: number
  current_ah?: number
  nominal_ah?: number
  starter_battery_voltage?: number
  dcdc?: ChargerStatus
  solar1?: ChargerStatus
  solar2?: ChargerStatus
  acdc?: ChargerStatus
  estimated_runtime_minutes?: number
  estimated_charge_time_minutes?: number
  avg_consumption_1h?: number
  avg_consumption_24h?: number
  timestamps?: DataTimestamps
}

interface ClimateUpdatePayload {
  mac: string
  name: string
  temperature?: number
  humidity?: number
  battery?: number
  updated_at: string
}

interface GPSUpdatePayload {
  latitude: number
  longitude: number
  timestamp: string
}

interface VehicleStateUpdatePayload {
  fuel_level_liters?: number
  fuel_level_percent?: number
  adblue_level_liters?: number
  adblue_level_percent?: number
  dpf_soot_level_percent?: number
  outdoor_temperature_c?: number
  estimated_range_km?: number
  timestamp: string
}

interface WSMessage {
  type: string
  payload: BatteryUpdatePayload | ClimateUpdatePayload | GPSUpdatePayload | VehicleStateUpdatePayload
}

type ConnectionStatus = 'connected' | 'disconnected' | 'connecting'

// A trip-stats payload omits any field the phone did not send. Keep the previous
// reading instead of substituting 0, which the card would display as a real level.
function mergeVehicleState(prev: VehicleState | null | undefined, payload: VehicleStateUpdatePayload): VehicleState {
  return {
    fuel_level_liters: payload.fuel_level_liters ?? prev?.fuel_level_liters,
    fuel_level_percent: payload.fuel_level_percent ?? prev?.fuel_level_percent,
    adblue_level_liters: payload.adblue_level_liters ?? prev?.adblue_level_liters,
    adblue_level_percent: payload.adblue_level_percent ?? prev?.adblue_level_percent,
    dpf_soot_level_percent: payload.dpf_soot_level_percent ?? prev?.dpf_soot_level_percent,
    outdoor_temperature_c: payload.outdoor_temperature_c ?? prev?.outdoor_temperature_c,
    estimated_range_km: payload.estimated_range_km ?? prev?.estimated_range_km,
    last_updated_at: payload.timestamp || prev?.last_updated_at || '',
  }
}

interface BatteryHistoryDataPoint {
  timestamp: string
  soc: number
  voltage: number
}

interface BatteryHistorySummary {
  current_soc: number
  avg_soc: number
  min_soc: number
  max_soc: number
  total_points: number
}

interface BatteryHistoryResponse {
  data_points: BatteryHistoryDataPoint[]
  summary: BatteryHistorySummary
}

interface HistoryDataPoint {
  timestamp: string
  value: number
}

interface ClimateSensorHistorySummary {
  current_temperature?: number
  current_humidity?: number
  avg_temperature?: number
  min_temperature?: number
  max_temperature?: number
  avg_humidity?: number
  min_humidity?: number
  max_humidity?: number
}

interface ClimateSensorHistory {
  mac: string
  name: string
  temperature: HistoryDataPoint[]
  humidity: HistoryDataPoint[]
  summary: ClimateSensorHistorySummary
}

interface TemperatureHistoryResponse {
  sensors: ClimateSensorHistory[]
}

function HistorySparkline({
  title,
  points,
  unit,
  pointClass,
}: {
  title: string
  points: HistoryDataPoint[]
  unit: string
  pointClass: string
}) {
  if (points.length === 0) {
    return null
  }
  const allValues = points.map((p) => p.value)
  const minVal = Math.floor(Math.min(...allValues) - 2)
  const maxVal = Math.ceil(Math.max(...allValues) + 2)
  const range = Math.max(1, maxVal - minVal)
  return (
    <div className="history-chart">
      <div className="chart-title">{title}</div>
      <div className="temp-history-chart">
        <div className="chart-guidelines">
          <div className="guideline" style={{ bottom: '100%' }}><span className="guideline-label">{maxVal}{unit}</span></div>
          <div className="guideline" style={{ bottom: '50%' }}><span className="guideline-label">{((maxVal + minVal) / 2).toFixed(0)}{unit}</span></div>
          <div className="guideline" style={{ bottom: '0%' }}><span className="guideline-label">{minVal}{unit}</span></div>
        </div>
        <div className="chart-points">
          {points.map((point, index) => (
            <div
              key={`${point.timestamp}-${index}`}
              className={`chart-point ${pointClass}`}
              style={{
                left: `${(index / Math.max(1, points.length - 1)) * 100}%`,
                bottom: `${((point.value - minVal) / range) * 100}%`,
              }}
              title={`${formatChartTime(point.timestamp)}: ${point.value.toFixed(1)}${unit}`}
            />
          ))}
        </div>
      </div>
      <div className="chart-x-labels">
        <span>{formatChartDate(points[0].timestamp)}</span>
        <span>{formatChartDate(points[points.length - 1].timestamp)}</span>
      </div>
    </div>
  )
}

function upsertClimateSensor(sensors: ClimateSensor[] | undefined, incoming: ClimateSensor): ClimateSensor[] {
  const list = [...(sensors ?? [])]
  const idx = list.findIndex((s) => s.mac === incoming.mac)
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...incoming }
  } else {
    list.push(incoming)
  }
  return list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}

type HistoryModalType = 'battery' | 'temperature' | null

const DASHBOARD_FETCH_ATTEMPTS = 3
const DASHBOARD_ATTEMPT_TIMEOUT_MS = 8000
const DASHBOARD_RETRY_MS = 2000

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'))
  }
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      window.clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

// fetchDashboardOnce issues one dashboard request. A per-attempt timeout is
// separate from `signal`, which cancels the request when a newer fetch starts.
async function fetchDashboardOnce(signal: AbortSignal): Promise<Response> {
  const attempt = new AbortController()
  const onAbort = () => attempt.abort()
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError')
  }
  signal.addEventListener('abort', onAbort)
  const timer = window.setTimeout(() => attempt.abort(), DASHBOARD_ATTEMPT_TIMEOUT_MS)
  try {
    return await fetch('/api/v1/dashboard', {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: attempt.signal,
    })
  } finally {
    window.clearTimeout(timer)
    signal.removeEventListener('abort', onAbort)
  }
}

export function Dashboard() {
  const location = useLocation()
  const { user } = useAuth()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const [errorTick, setErrorTick] = useState(0)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const reconnectDelayRef = useRef(WEBSOCKET.INITIAL_RECONNECT_DELAY)
  const mountedRef = useRef(true)
  const dashboardAbortRef = useRef<AbortController | null>(null)
  const hasDashboardDataRef = useRef(false)

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting')
  const [historyModal, setHistoryModal] = useState<HistoryModalType>(null)
  const [batteryHistory, setBatteryHistory] = useState<BatteryHistoryResponse | null>(null)
  const [temperatureHistory, setTemperatureHistory] = useState<TemperatureHistoryResponse | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [showIdleChargers, setShowIdleChargers] = useState(false)
  const [, setTick] = useState(0) // Force re-render for staleness checks

  const fetchDashboard = useCallback(async (maxAttempts = DASHBOARD_FETCH_ATTEMPTS) => {
    // Opening the app fires several fetches at once (mount, focus, websocket).
    // A slow failure from an earlier request must not replace a later success.
    dashboardAbortRef.current?.abort()
    const controller = new AbortController()
    dashboardAbortRef.current = controller

    let lastMessage = 'Failed to fetch dashboard data'
    let lastStatus: number | null = null

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (controller.signal.aborted) return
      try {
        const response = await fetchDashboardOnce(controller.signal)
        if (controller.signal.aborted) return
        if (!response.ok) {
          lastStatus = response.status
          const body = await response.json().catch(() => null) as { error?: string } | null
          lastMessage = body?.error || 'Failed to fetch dashboard data'
          const retryable = response.status === 401 || response.status >= 500
          if (retryable && attempt < maxAttempts - 1) {
            await sleep(TIMEOUTS.AUTH_RETRY_DELAY * (attempt + 1), controller.signal)
            continue
          }
          break
        }
        const dashboardData = await response.json()
        if (controller.signal.aborted) return
        hasDashboardDataRef.current = true
        setData(dashboardData)
        setError(null)
        setErrorStatus(null)
        setLoading(false)
        return
      } catch (err) {
        if (controller.signal.aborted) return
        lastStatus = null
        lastMessage = err instanceof Error && err.name !== 'AbortError'
          ? err.message
          : 'Failed to fetch dashboard data'
        if (attempt < maxAttempts - 1) {
          try {
            await sleep(TIMEOUTS.AUTH_RETRY_DELAY * (attempt + 1), controller.signal)
          } catch {
            return
          }
          continue
        }
      }
    }

    if (controller.signal.aborted) return
    setLoading(false)
    // Keep showing the last good dashboard if a later refresh fails.
    if (hasDashboardDataRef.current) {
      console.error('Dashboard refresh failed:', lastMessage)
      return
    }
    setError(lastMessage)
    setErrorStatus(lastStatus)
    setErrorTick((tick) => tick + 1)
  }, [])

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/settings')
      if (response.ok) {
        const settings = await response.json()
        setShowIdleChargers(settings.show_idle_chargers ?? false)
      }
    } catch {
      // Ignore - use default
    }
  }, [])

  const fetchBatteryHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const response = await fetch('/api/v1/history/battery')
      if (!response.ok) {
        throw new Error('Failed to fetch battery history')
      }
      const historyData = await response.json()
      setBatteryHistory(historyData)
    } catch (err) {
      console.error('Failed to fetch battery history:', err)
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  const fetchTemperatureHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const response = await fetch('/api/v1/history/temperature')
      if (!response.ok) {
        throw new Error('Failed to fetch temperature history')
      }
      const historyData = await response.json()
      setTemperatureHistory(historyData)
    } catch (err) {
      console.error('Failed to fetch temperature history:', err)
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  const openHistoryModal = (type: HistoryModalType) => {
    setHistoryModal(type)
    if (type === 'battery') {
      fetchBatteryHistory()
    } else if (type === 'temperature') {
      fetchTemperatureHistory()
    }
  }

  const closeHistoryModal = () => {
    setHistoryModal(null)
  }

  useEffect(() => {
    if (!user) return
    if (location.pathname === '/') {
      fetchDashboard()
    }
  }, [user, location.pathname, fetchDashboard])

  // A failed first load used to stay on the error screen until a manual reload.
  // Keep trying transient failures (offline radio, server still starting, locked DB).
  // A 401 that survives the in-call retries is an auth failure, not a blip.
  useEffect(() => {
    if (!user || !error) return
    if (errorStatus === 401) return
    const id = window.setTimeout(() => {
      fetchDashboard(1)
    }, DASHBOARD_RETRY_MS)
    return () => window.clearTimeout(id)
  }, [user, error, errorStatus, errorTick, fetchDashboard])

  useEffect(() => {
    if (!user) return
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboard()
      }
    }
    const handlePageShow = () => {
      fetchDashboard()
    }

    document.addEventListener('visibilitychange', refreshIfVisible)
    window.addEventListener('focus', refreshIfVisible)
    window.addEventListener('pageshow', handlePageShow)
    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible)
      window.removeEventListener('focus', refreshIfVisible)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [user, fetchDashboard])

  // Tick interval for staleness re-check
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), INTERVALS.STALENESS_CHECK)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!user) return
    mountedRef.current = true
    fetchDashboard()
    fetchSettings()

    const connectWebSocket = () => {
      if (!mountedRef.current) return

      setConnectionStatus('connecting')
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws`)

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close()
          return
        }
        console.log('WebSocket connected')
        setConnectionStatus('connected')
        reconnectDelayRef.current = WEBSOCKET.INITIAL_RECONNECT_DELAY
        fetchDashboard()
      }

      ws.onmessage = (event) => {
        try {
          const message: WSMessage = JSON.parse(event.data)
          if (message.type === 'battery_update') {
            const payload = message.payload as BatteryUpdatePayload
            setData((prev) => ({
              battery: {
                soc: payload.soc,
                voltage: payload.voltage,
                current: payload.current,
                temperature: payload.temperature,
                power_watts: payload.power_watts,
                current_ah: payload.current_ah,
                nominal_ah: payload.nominal_ah,
                starter_battery_voltage: payload.starter_battery_voltage,
                dcdc: payload.dcdc,
                solar1: payload.solar1,
                solar2: payload.solar2,
                acdc: payload.acdc,
                estimated_runtime_minutes: payload.estimated_runtime_minutes,
                estimated_charge_time_minutes: payload.estimated_charge_time_minutes,
                avg_consumption_1h: payload.avg_consumption_1h,
                avg_consumption_24h: payload.avg_consumption_24h,
                timestamps: payload.timestamps,
              },
              water: prev?.water ?? null,
              climate_sensors: prev?.climate_sensors ?? [],
              propane_days: prev?.propane_days ?? null,
              location: prev?.location ?? null,
              vehicle_state: prev?.vehicle_state ?? null,
              last_updated_at: payload.timestamp,
            }))
          } else if (message.type === 'temperature_update') {
            const payload = message.payload as ClimateUpdatePayload
            const incoming: ClimateSensor = {
              mac: payload.mac,
              name: payload.name,
              temperature: payload.temperature,
              humidity: payload.humidity,
              battery: payload.battery,
              updated_at: payload.updated_at,
            }
            setData((prev) => ({
              battery: prev?.battery ?? null,
              water: prev?.water ?? null,
              climate_sensors: upsertClimateSensor(prev?.climate_sensors, incoming),
              propane_days: prev?.propane_days ?? null,
              location: prev?.location ?? null,
              vehicle_state: prev?.vehicle_state ?? null,
              last_updated_at: payload.updated_at,
            }))
          } else if (message.type === 'gps_update') {
            const payload = message.payload as GPSUpdatePayload
            setData((prev) => ({
              battery: prev?.battery ?? null,
              water: prev?.water ?? null,
              climate_sensors: prev?.climate_sensors ?? [],
              propane_days: prev?.propane_days ?? null,
              location: {
                latitude: payload.latitude,
                longitude: payload.longitude,
              },
              vehicle_state: prev?.vehicle_state ?? null,
              last_updated_at: payload.timestamp,
            }))
          } else if (message.type === 'vehicle_state_update') {
            const payload = message.payload as VehicleStateUpdatePayload
            setData((prev) => ({
              battery: prev?.battery ?? null,
              water: prev?.water ?? null,
              climate_sensors: prev?.climate_sensors ?? [],
              propane_days: prev?.propane_days ?? null,
              location: prev?.location ?? null,
              vehicle_state: mergeVehicleState(prev?.vehicle_state, payload),
              last_updated_at: payload.timestamp,
            }))
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
        }
      }

      ws.onclose = () => {
        if (!mountedRef.current) return
        console.log('WebSocket disconnected')
        setConnectionStatus('disconnected')
        wsRef.current = null

        const delay = reconnectDelayRef.current
        console.log(`Reconnecting in ${delay / 1000}s...`)
        reconnectTimeoutRef.current = window.setTimeout(() => {
          if (mountedRef.current) {
            reconnectDelayRef.current = Math.min(
              reconnectDelayRef.current * WEBSOCKET.RECONNECT_MULTIPLIER,
              WEBSOCKET.MAX_RECONNECT_DELAY
            )
            connectWebSocket()
          }
        }, delay)
      }

      ws.onerror = (err) => {
        console.error('WebSocket error:', err)
      }

      wsRef.current = ws
    }

    connectWebSocket()

    return () => {
      mountedRef.current = false
      dashboardAbortRef.current?.abort()
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [user, fetchDashboard, fetchSettings])

  const getSOCColorClass = (soc: number): string => {
    if (soc < THRESHOLDS.BATTERY_DANGER) return 'progress-bar--danger'
    if (soc <= THRESHOLDS.BATTERY_WARNING) return 'progress-bar--warning'
    return 'progress-bar--success'
  }

  const getFreshWaterColorClass = (percent: number): string => {
    if (percent < THRESHOLDS.FRESH_WATER_DANGER) return 'progress-bar--danger'
    if (percent <= THRESHOLDS.FRESH_WATER_WARNING) return 'progress-bar--warning'
    return 'progress-bar--success'
  }

  const getGreyWaterColorClass = (percent: number): string => {
    if (percent > THRESHOLDS.GREY_WATER_DANGER) return 'progress-bar--danger'
    if (percent >= THRESHOLDS.GREY_WATER_WARNING) return 'progress-bar--warning'
    return 'progress-bar--success'
  }

  const isBatteryWarning = (soc: number): boolean => soc < THRESHOLDS.BATTERY_DANGER
  const isFreshWaterWarning = (percent: number): boolean => percent < THRESHOLDS.FRESH_WATER_DANGER
  const isGreyWaterFull = (percent: number): boolean => percent >= THRESHOLDS.GREY_WATER_FULL

  const shouldShowCharger = (charger?: ChargerStatus): boolean => {
    if (!charger) return false
    const hasOutput = (charger.out_watts !== undefined && charger.out_watts > 0) ||
                      (charger.out_amps !== undefined && charger.out_amps > 0)
    return hasOutput || showIdleChargers
  }

  const formatChargerBadge = (label: string, charger: ChargerStatus): string => {
    const parts: string[] = []
    if (charger.out_watts !== undefined) {
      parts.push(`${charger.out_watts.toFixed(PRECISION.POWER_WATTS)}W`)
    } else if (charger.out_amps !== undefined && charger.out_volts !== undefined) {
      parts.push(`${(charger.out_amps * charger.out_volts).toFixed(PRECISION.POWER_WATTS)}W`)
    }
    if (charger.in_watts !== undefined && charger.out_watts !== undefined) {
      const efficiency = (charger.out_watts / charger.in_watts * 100).toFixed(0)
      parts.push(`${efficiency}%`)
    }
    return parts.length > 0 ? `${label} · ${parts.join(' ')}` : label
  }

  const formatHouseVoltage = (volts: number): string => `${volts.toFixed(2)} V`

  const formatSigned = (value: number, digits: number, unit: string, showPlus = false): string => {
    const sign = value > 0 && showPlus ? '+' : value < 0 ? '\u2212' : ''
    return `${sign}${Math.abs(value).toFixed(digits)} ${unit}`
  }

  const formatPowerWatts = (watts: number): string => formatSigned(watts, PRECISION.POWER_WATTS, 'W', true)

  const powerClass = (watts: number): string => {
    if (watts < 0) return 'text-warning'
    if (watts > 0) return 'text-success'
    return 'text-muted'
  }

  // Wrapper component for stale data display
  const StaleWrapper = ({ timestamp, children, className = '' }: { timestamp?: string; children: ReactNode; className?: string }) => {
    const stale = isStale(timestamp, THRESHOLDS.STALENESS_MINUTES)
    return (
      <div className={[className, stale ? 'data-stale' : ''].filter(Boolean).join(' ')}>
        {children}
        {stale && timestamp && <span className="stale-age">{formatAge(timestamp)}</span>}
      </div>
    )
  }

  // Wrapper for charger badges with per-charger staleness
  const StaleChargerBadge = ({ timestamp, label, charger, badgeClass }: {
    timestamp?: string
    label: string
    charger: ChargerStatus
    badgeClass: string
  }) => {
    const stale = isStale(timestamp, THRESHOLDS.STALENESS_MINUTES)
    return (
      <span className={`source-badge ${badgeClass} ${stale ? 'data-stale' : ''}`}>
        {formatChargerBadge(label, charger)}
        {stale && timestamp && <span className="stale-age">{formatAge(timestamp)}</span>}
      </span>
    )
  }

  if (loading && !data) {
    return (
      <div className="page">
        <h1>Dashboard</h1>
        <p>Loading...</p>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="page">
        <h1>Dashboard</h1>
        <p className="text-danger">Error: {error}</p>
        {errorStatus === 401 ? (
          <button type="button" onClick={() => { fetchDashboard() }}>Retry</button>
        ) : (
          <p>Retrying...</p>
        )}
      </div>
    )
  }

  const battery = data?.battery ?? null
  const showBatterySources = !!battery && (
    shouldShowCharger(battery.dcdc) ||
    shouldShowCharger(battery.solar1) ||
    shouldShowCharger(battery.solar2) ||
    shouldShowCharger(battery.acdc)
  )
  const showBatteryFooter = !!battery && (
    battery.estimated_charge_time_minutes !== undefined ||
    battery.current !== undefined ||
    battery.current_ah !== undefined ||
    battery.nominal_ah !== undefined ||
    battery.temperature !== undefined ||
    battery.starter_battery_voltage !== undefined
  )

  return (
    <div className="page dashboard">
      <h1>Dashboard</h1>

      <div className="dashboard-grid">
        {/* Battery Section */}
        <div
          className={`card dashboard-card dashboard-card--tappable ${battery && isBatteryWarning(battery.soc) ? 'card--warning' : ''}`}
          onClick={() => openHistoryModal('battery')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && openHistoryModal('battery')}
        >
          <div className="card-header">
            <h3 className="card-title">Battery</h3>
            <div className="card-header-badges">
              <span className="badge badge--primary tap-hint">Tap for history</span>
              {battery && isBatteryWarning(battery.soc) && (
                <span className="badge badge--danger">Low</span>
              )}
            </div>
          </div>
          {battery ? (
            <div className="battery-status">
              <StaleWrapper timestamp={battery.timestamps?.core_battery}>
                <div className="battery-hero">
                  <div className="battery-soc">
                    <span className="battery-kicker">State of charge</span>
                    <div className="battery-soc-row">
                      <span className={`battery-soc-value ${isBatteryWarning(battery.soc) ? 'text-danger' : ''}`}>
                        {battery.soc.toFixed(PRECISION.BATTERY_SOC)}
                      </span>
                      <span className="battery-soc-unit">%</span>
                    </div>
                  </div>
                  {battery.power_watts !== undefined && (
                    <div className="battery-power">
                      <span className="battery-kicker">Power</span>
                      <span className={`battery-power-value ${powerClass(battery.power_watts)}`}>
                        {formatPowerWatts(battery.power_watts)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="progress battery-progress">
                  <div
                    className={`progress-bar ${getSOCColorClass(battery.soc)}`}
                    style={{ width: `${Math.min(100, Math.max(0, battery.soc))}%` }}
                  />
                </div>
              </StaleWrapper>

              <div className={`battery-tiles${showBatterySources ? '' : ' battery-tiles--single'}`}>
                <StaleWrapper timestamp={battery.timestamps?.core_battery}>
                  <div className="battery-tile">
                    <span className="battery-kicker">House voltage</span>
                    <span className="battery-tile-value">{formatHouseVoltage(battery.voltage)}</span>
                  </div>
                </StaleWrapper>
                {showBatterySources && (
                  <div className="battery-tile">
                    <span className="battery-kicker">Sources</span>
                    <div className="source-badges">
                      {shouldShowCharger(battery.dcdc) && battery.dcdc && (
                        <StaleChargerBadge timestamp={battery.timestamps?.dcdc} label="DC-DC" charger={battery.dcdc} badgeClass="source-badge--dcdc" />
                      )}
                      {shouldShowCharger(battery.solar1) && battery.solar1 && (
                        <StaleChargerBadge timestamp={battery.timestamps?.solar1} label="Solar 1" charger={battery.solar1} badgeClass="source-badge--solar" />
                      )}
                      {shouldShowCharger(battery.solar2) && battery.solar2 && (
                        <StaleChargerBadge timestamp={battery.timestamps?.solar2} label="Solar 2" charger={battery.solar2} badgeClass="source-badge--solar" />
                      )}
                      {shouldShowCharger(battery.acdc) && battery.acdc && (
                        <StaleChargerBadge timestamp={battery.timestamps?.acdc} label="AC-DC" charger={battery.acdc} badgeClass="source-badge--acdc" />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {showBatteryFooter && (
                <div className="battery-footer">
                  {battery.estimated_charge_time_minutes !== undefined && (
                    <StaleWrapper timestamp={battery.timestamps?.core_battery} className="battery-footer-wrap">
                      <span className="battery-footer-item">
                        Charge time <span className="battery-footer-value text-success">{formatRuntime(battery.estimated_charge_time_minutes)}</span>
                      </span>
                    </StaleWrapper>
                  )}
                  {battery.current !== undefined && (
                    <StaleWrapper timestamp={battery.timestamps?.core_battery} className="battery-footer-wrap">
                      <span className="battery-footer-item">
                        Current <span className="battery-footer-value">{formatSigned(battery.current, PRECISION.BATTERY_CURRENT, 'A')}</span>
                      </span>
                    </StaleWrapper>
                  )}
                  {(battery.current_ah !== undefined || battery.nominal_ah !== undefined) && (
                    <StaleWrapper timestamp={battery.timestamps?.core_battery} className="battery-footer-wrap">
                      <span className="battery-footer-item">
                        Capacity <span className="battery-footer-value">
                          {battery.current_ah !== undefined ? battery.current_ah.toFixed(0) : '--'} / {battery.nominal_ah !== undefined ? battery.nominal_ah.toFixed(0) : '--'} Ah
                        </span>
                      </span>
                    </StaleWrapper>
                  )}
                  {battery.temperature !== undefined && (
                    <StaleWrapper timestamp={battery.timestamps?.core_battery} className="battery-footer-wrap">
                      <span className="battery-footer-item">
                        Temp <span className="battery-footer-value">{battery.temperature.toFixed(PRECISION.TEMPERATURE)}°C</span>
                      </span>
                    </StaleWrapper>
                  )}
                  {battery.starter_battery_voltage !== undefined && (
                    <StaleWrapper timestamp={battery.timestamps?.starter_battery} className="battery-footer-wrap">
                      <span className="battery-footer-item">
                        Starter <span className="battery-footer-value">{battery.starter_battery_voltage.toFixed(2)} V</span>
                      </span>
                    </StaleWrapper>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="no-data">
              <p className="text-muted">No battery data available</p>
            </div>
          )}
        </div>

        {/* Water Levels Section */}
        <div className={`card dashboard-card ${
          data?.water && (isFreshWaterWarning(data.water.fresh_percent) || isGreyWaterFull(data.water.grey_percent))
            ? 'card--warning'
            : ''
        }`}>
          <div className="card-header">
            <h3 className="card-title">Water Levels</h3>
            <div className="card-header-badges">
              <span className="badge badge--muted">estimated</span>
              {data?.water && isFreshWaterWarning(data.water.fresh_percent) && (
                <span className="badge badge--danger">Low Fresh</span>
              )}
              {data?.water && isGreyWaterFull(data.water.grey_percent) && (
                <span className="badge badge--danger">Grey Full</span>
              )}
            </div>
          </div>
          {data?.water ? (
            <div className="water-status">
              <div className="water-tank">
                <div className="water-tank-header">
                  <span className="water-tank-label">Fresh Water</span>
                  <span className={`water-tank-value ${isFreshWaterWarning(data.water.fresh_percent) ? 'text-danger' : ''}`}>
                    {Math.round(data.water.fresh_percent)}%
                  </span>
                </div>
                <div className="progress water-progress">
                  <div
                    className={`progress-bar ${getFreshWaterColorClass(data.water.fresh_percent)}`}
                    style={{ width: `${Math.min(100, Math.max(0, data.water.fresh_percent))}%` }}
                  />
                </div>
              </div>
              <div className="water-tank">
                <div className="water-tank-header">
                  <span className="water-tank-label">Grey Water</span>
                  <span className={`water-tank-value ${isGreyWaterFull(data.water.grey_percent) ? 'text-danger' : ''}`}>
                    {Math.round(data.water.grey_percent)}%
                  </span>
                </div>
                <div className="progress water-progress">
                  <div
                    className={`progress-bar ${getGreyWaterColorClass(data.water.grey_percent)}`}
                    style={{ width: `${Math.min(100, Math.max(0, data.water.grey_percent))}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data">
              <p className="text-muted">No water data available</p>
            </div>
          )}
        </div>

        {/* Propane Section */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Propane</h3>
          </div>
          <div className="propane-temp-status">
            <div className="status-item">
              <div className="status-icon">🔥</div>
              <div className="status-content">
                <span className="status-label">Propane</span>
                {data?.propane_days !== null && data?.propane_days !== undefined ? (
                  <span className="status-value">
                    {data.propane_days} <span className="status-unit">days</span>
                  </span>
                ) : (
                  <span className="status-value text-muted">--</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Climate sensors */}
        <div
          className="card dashboard-card dashboard-card--tappable"
          onClick={() => openHistoryModal('temperature')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && openHistoryModal('temperature')}
        >
          <div className="card-header">
            <h3 className="card-title">Climate</h3>
            <span className="badge badge--primary tap-hint">Tap for history</span>
          </div>
          {data?.climate_sensors && data.climate_sensors.length > 0 ? (
            <div className="climate-sensor-list">
              {data.climate_sensors.map((sensor) => (
                <StaleWrapper key={sensor.mac} timestamp={sensor.updated_at}>
                  <div className="status-item">
                    <div className="status-icon">🌡️</div>
                    <div className="status-content">
                      <span className="status-label">{sensor.name}</span>
                      <span className="status-value">
                        {sensor.temperature !== undefined ? (
                          <>
                            {sensor.temperature.toFixed(PRECISION.TEMPERATURE)} <span className="status-unit">°C</span>
                          </>
                        ) : (
                          <span className="text-muted">--</span>
                        )}
                        {sensor.humidity !== undefined && (
                          <span className="climate-humidity">
                            {sensor.humidity.toFixed(PRECISION.TEMPERATURE)}%
                          </span>
                        )}
                      </span>
                      {sensor.battery !== undefined && sensor.battery < THRESHOLDS.SENSOR_BATTERY_LOW && (
                        <span className="climate-battery-low">Battery {Math.round(sensor.battery)}%</span>
                      )}
                    </div>
                  </div>
                </StaleWrapper>
              ))}
            </div>
          ) : (
            <div className="no-data">
              <p className="text-muted">No climate sensors yet</p>
            </div>
          )}
        </div>

        {/* Location Section */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Location</h3>
          </div>
          {data?.location ? (
            <div className="location-status">
              <div className="status-item">
                <div className="status-icon">📍</div>
                <div className="status-content">
                  <span className="status-label">Coordinates</span>
                  <span className="status-value location-coords">
                    {data.location.latitude.toFixed(PRECISION.LOCATION)}, {data.location.longitude.toFixed(PRECISION.LOCATION)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data">
              <p className="text-muted">No location data available</p>
            </div>
          )}
        </div>

        {/* Vehicle State Section */}
        <VehicleStateCard vehicleState={data?.vehicle_state ?? null} />
      </div>

      {/* Connection Status & Last Updated */}
      <div className="dashboard-footer">
        <div className={`connection-status connection-status--${connectionStatus}`}>
          <span className="connection-dot" />
          <span className="connection-label">
            {connectionStatus === 'connected' && 'Live'}
            {connectionStatus === 'connecting' && 'Connecting...'}
            {connectionStatus === 'disconnected' && 'Reconnecting...'}
          </span>
        </div>
        {data?.last_updated_at && (
          <div className="last-updated">
            Last updated: {new Date(data.last_updated_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* Battery History Modal */}
      {historyModal === 'battery' && (
        <Modal title="Battery History (Last 7 Days)" onClose={closeHistoryModal} variant="form">
          {historyLoading ? (
            <div className="history-loading">
              <p>Loading history...</p>
            </div>
          ) : batteryHistory ? (
            <div className="history-content">
              <div className="history-chart">
                <div className="chart-title">State of Charge Over Time</div>
                <div className="soc-history-chart">
                  <div className="chart-guidelines">
                    <div className="guideline" style={{ bottom: '100%' }}><span className="guideline-label">100%</span></div>
                    <div className="guideline" style={{ bottom: '75%' }}><span className="guideline-label">75%</span></div>
                    <div className="guideline" style={{ bottom: '50%' }}><span className="guideline-label">50%</span></div>
                    <div className="guideline" style={{ bottom: '25%' }}><span className="guideline-label">25%</span></div>
                    <div className="guideline" style={{ bottom: '0%' }}><span className="guideline-label">0%</span></div>
                  </div>
                  <div className="chart-points">
                    {batteryHistory.data_points.map((point, index) => (
                      <div
                        key={index}
                        className={`chart-point ${point.soc < THRESHOLDS.BATTERY_DANGER ? 'chart-point--danger' : point.soc < THRESHOLDS.BATTERY_WARNING ? 'chart-point--warning' : 'chart-point--success'}`}
                        style={{
                          left: `${(index / Math.max(1, batteryHistory.data_points.length - 1)) * 100}%`,
                          bottom: `${point.soc}%`,
                        }}
                        title={`${formatChartTime(point.timestamp)}: ${point.soc.toFixed(2)}% (${point.voltage.toFixed(PRECISION.BATTERY_VOLTAGE)}V)`}
                      />
                    ))}
                  </div>
                </div>
                <div className="chart-x-labels">
                  {batteryHistory.data_points.length > 0 && (
                    <>
                      <span>{formatChartDate(batteryHistory.data_points[0].timestamp)}</span>
                      <span>{formatChartDate(batteryHistory.data_points[batteryHistory.data_points.length - 1].timestamp)}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="history-summary">
                <div className="summary-item">
                  <span className="summary-label">Current</span>
                  <span className="summary-value">{batteryHistory.summary.current_soc.toFixed(1)}%</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Average</span>
                  <span className="summary-value">{batteryHistory.summary.avg_soc.toFixed(1)}%</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Min</span>
                  <span className="summary-value">{batteryHistory.summary.min_soc.toFixed(1)}%</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Max</span>
                  <span className="summary-value">{batteryHistory.summary.max_soc.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data">
              <p className="text-muted">No history data available</p>
            </div>
          )}
        </Modal>
      )}

      {/* Climate History Modal */}
      {historyModal === 'temperature' && (
        <Modal title="Climate History (Last 7 Days)" onClose={closeHistoryModal} variant="form">
          {historyLoading ? (
            <div className="history-loading">
              <p>Loading history...</p>
            </div>
          ) : temperatureHistory && temperatureHistory.sensors.length > 0 ? (
            <div className="history-content">
              {temperatureHistory.sensors.map((sensor) => (
                <div key={sensor.mac} className="climate-history-sensor">
                  <div className="summary-section-title">{sensor.name}</div>
                  <HistorySparkline
                    title="Temperature"
                    points={sensor.temperature}
                    unit="°C"
                    pointClass="chart-point--indoor"
                  />
                  <HistorySparkline
                    title="Humidity"
                    points={sensor.humidity}
                    unit="%"
                    pointClass="chart-point--outdoor"
                  />
                  <div className="history-summary history-summary--temp">
                    {sensor.summary.current_temperature !== undefined && (
                      <div className="summary-section">
                        <div className="summary-section-title">Temperature</div>
                        <div className="summary-grid">
                          <div className="summary-item">
                            <span className="summary-label">Current</span>
                            <span className="summary-value">{sensor.summary.current_temperature?.toFixed(1)}°C</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Avg</span>
                            <span className="summary-value">{sensor.summary.avg_temperature?.toFixed(1)}°C</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Min</span>
                            <span className="summary-value">{sensor.summary.min_temperature?.toFixed(1)}°C</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Max</span>
                            <span className="summary-value">{sensor.summary.max_temperature?.toFixed(1)}°C</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {sensor.summary.current_humidity !== undefined && (
                      <div className="summary-section">
                        <div className="summary-section-title">Humidity</div>
                        <div className="summary-grid">
                          <div className="summary-item">
                            <span className="summary-label">Current</span>
                            <span className="summary-value">{sensor.summary.current_humidity?.toFixed(1)}%</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Avg</span>
                            <span className="summary-value">{sensor.summary.avg_humidity?.toFixed(1)}%</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Min</span>
                            <span className="summary-value">{sensor.summary.min_humidity?.toFixed(1)}%</span>
                          </div>
                          <div className="summary-item">
                            <span className="summary-label">Max</span>
                            <span className="summary-value">{sensor.summary.max_humidity?.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data">
              <p className="text-muted">No climate history available</p>
            </div>
          )}
        </Modal>
      )}
    </div>
  )
}
